import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { api } from '../api/client';
import type { UpcomingTH } from '../types';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';

interface Props {
  onOpenSession: (sessionId: number) => void;
}

interface PastTH {
  session_id: number | null;   // null = past BSP never opened locally
  v2_session_id: number | null;
  scheduled_date: string;
  title: string | null;
  location: string | null;
  status: 'open' | 'closed' | null;
  session_type?: 'th' | 'bsp' | 'intake_bsp' | 'journal_import';
  v2_appointment_id?: number | null;
  v2_participant_id?: number | null;
  participant_name?: string | null;
  start_time?: string | null;
  participant_names?: string[];
  participant_count?: number | null;
  recorded_count?: number | null;
  animator_user_id?: number | null;
  animator_name?: string | null;
}

interface TrashedTH {
  session_id: number;
  session_type: string;
  scheduled_date: string;
  title: string | null;
  location: string | null;
  animator_name: string;
  deleted_at: string;
  deleted_by_name: string | null;
  participant_count: number;
}

const SCOPE_KEY = 'evolution.upcoming.scope';

/** Calendar-leaf date block: weekday, day number, month. */
function CalDate({ iso, lang }: { iso: string; lang: string }) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const loc = lang === 'fr' ? 'fr-CH' : 'en-CH';
  return (
    <div className="cal-date" aria-hidden="true">
      <span className="cal-date-dow">{date.toLocaleDateString(loc, { weekday: 'short' }).replace('.', '')}</span>
      <span className="cal-date-num">{d}</span>
      <span className="cal-date-mon">{date.toLocaleDateString(loc, { month: 'short' })}</span>
    </div>
  );
}

/** Complete / partial / to-record indicator for a session's entries. */
function FillPill({
  recorded, total, t,
}: { recorded: number | null | undefined; total: number | null | undefined; t: (k: string) => string }) {
  if (total == null || total === 0) return null;
  if (recorded == null) {
    return <span className="fill-pill fill-pill--todo">{t('upcoming.fill.todo')}</span>;
  }
  if (recorded >= total) {
    return <span className="fill-pill fill-pill--done">✓ {t('upcoming.fill.complete')}</span>;
  }
  if (recorded > 0) {
    return (
      <span className="fill-pill fill-pill--partial">
        {t('upcoming.fill.partial').replace('{a}', String(recorded)).replace('{b}', String(total))}
      </span>
    );
  }
  return <span className="fill-pill fill-pill--todo">{t('upcoming.fill.todo')} · 0/{total}</span>;
}

/** Participant names, capped with a +n overflow. */
function NamesLine({ names }: { names: string[] | undefined }) {
  if (!names || names.length === 0) return null;
  const MAX = 6;
  const shown = names.slice(0, MAX);
  const more = names.length - shown.length;
  return (
    <div className="th-card-names">
      {shown.join(', ')}
      {more > 0 && <span className="th-card-names-more"> +{more}</span>}
    </div>
  );
}

const PROMINENT_COUNT = 2;

function useFormatDate() {
  const { lang } = useT();
  return (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };
}

function localTodayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function UpcomingPage({ onOpenSession }: Props) {
  const { t, lang } = useT();
  const { user } = useAuth();
  const formatDate = useFormatDate();
  const [scope, setScope] = useState<'mine' | 'all'>(() => (localStorage.getItem(SCOPE_KEY) === 'all' ? 'all' : 'mine'));
  const [items, setItems] = useState<UpcomingTH[] | null>(null);
  const [past, setPast] = useState<PastTH[] | null>(null);
  const [trash, setTrash] = useState<TrashedTH[] | null>(null);
  const [showTrash, setShowTrash] = useState(false);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newType, setNewType] = useState<'th' | 'bsp'>('th');
  const [busy, setBusy] = useState(false);
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  useEffect(() => { localStorage.setItem(SCOPE_KEY, scope); }, [scope]);

  const keyFor = (item: UpcomingTH) =>
    item.source === 'bsp'
      ? `bsp:${item.v2_appointment_id ?? item.session_id}`
      : `${item.v2_session_id ?? 'adhoc'}:${item.scheduled_date}:${item.session_id ?? ''}`;

  const formatTime = (t?: string | null) => (t ? t.slice(0, 5) : null);

  const load = async () => {
    const qs = scope === 'all' ? '?all=1' : '';
    const [up, pa, tr] = await Promise.all([
      api.get<UpcomingTH[]>(`/api/th/upcoming${qs}`),
      api.get<PastTH[]>(`/api/th/past?limit=100${scope === 'all' ? '&all=1' : ''}`),
      api.get<TrashedTH[]>('/api/th/trash'),
    ]);
    setItems(up);
    setPast(pa);
    setTrash(tr);
  };

  useEffect(() => { setItems(null); setPast(null); load(); }, [scope]);   // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (item: UpcomingTH) => {
    if (openingKey) return; // one open at a time — no double-fire
    setOpeningKey(keyFor(item));
    try {
      let sessionId = item.session_id;
      if (!sessionId) {
        if (item.source === 'bsp') {
          // A BSP becomes a regular session row with its single participant.
          const created = await api.post<{ id: number }>('/api/th/sessions', {
            v2_appointment_id: item.v2_appointment_id,
            v2_participant_id: item.v2_participant_id,
            participant_name: item.participant_name,
            scheduled_date: item.scheduled_date,
            title: item.title,
          });
          sessionId = created.id;
        } else {
          const created = await api.post<{ id: number }>('/api/th/sessions', {
            v2_session_id: item.v2_session_id,
            scheduled_date: item.scheduled_date,
            title: item.title,
            location: item.location,
          });
          sessionId = created.id;
          if (item.v2_session_id) {
            await api.post(`/api/th/sessions/${sessionId}/hydrate-participants`).catch(() => {});
          }
        }
      }
      onOpenSession(sessionId!);
    } finally {
      setOpeningKey(null);
    }
  };

  const createAdHoc = async () => {
    if (!newTitle.trim() || !newDate) return;
    setBusy(true);
    try {
      const created = await api.post<{ id: number }>('/api/th/sessions', {
        v2_session_id: null,
        scheduled_date: newDate,
        title: newTitle.trim(),
        location: newLocation.trim() || null,
        session_type: newType,
      });
      setShowNew(false);
      setNewTitle(''); setNewDate(''); setNewLocation(''); setNewType('th');
      onOpenSession(created.id);
    } finally {
      setBusy(false);
    }
  };

  const restore = async (sessionId: number) => {
    await api.post(`/api/th/sessions/${sessionId}/restore`);
    load();
  };

  const sourceLabel = (s: UpcomingTH['source']) =>
    s === 'recurrence' ? t('upcoming.source.recurrence')
      : s === 'adhoc' ? t('upcoming.source.adhoc')
      : s === 'bsp' ? t('upcoming.source.bsp')
      : t('upcoming.source.scheduled');

  /** Who animates, when it is not me (always shown in the "all" view). */
  const whoLine = (animatorUserId: number | null | undefined, animatorName: string | null | undefined) => {
    if (!animatorName) return null;
    if (animatorUserId != null && animatorUserId === user?.id && scope === 'mine') return null;
    return <span className="th-card-who">{animatorName}</span>;
  };

  // Past entries: an unopened BSP has no session yet — create it on click,
  // mirroring open() above, then enter it through the same flow.
  const pastKeyFor = (p: PastTH) =>
    p.session_id != null ? `past:${p.session_id}`
      : p.session_type === 'intake_bsp' ? `past-intake:${p.v2_participant_id}`
      : p.session_type === 'th' ? `past-th:${p.v2_session_id}:${p.scheduled_date}`
      : `past-bsp:${p.v2_appointment_id}`;

  const openPast = async (p: PastTH) => {
    if (p.session_id != null) {
      onOpenSession(p.session_id);
      return;
    }
    // A premier entretien / BSP without a materialized séance is a read-only
    // historical marker (no Éveil appointment exists for it) — nothing to create.

    if (openingKey) return;
    setOpeningKey(pastKeyFor(p));
    try {
      if (p.session_type === 'intake_bsp') {
        const created = await api.post<{id:number}>('/api/quality/intake', { participant_id:p.v2_participant_id, date:p.scheduled_date });
        onOpenSession(created.id); return;
      }
      if (p.session_type === 'th') {
        // A held séance never opened here: hydrate it from Éveil, as upcoming does.
        const created = await api.post<{ id: number }>('/api/th/sessions', {
          v2_session_id: p.v2_session_id,
          scheduled_date: p.scheduled_date,
          title: p.title,
          location: p.location,
        });
        onOpenSession(created.id);
        return;
      }
      const created = await api.post<{ id: number }>('/api/th/sessions', {
        v2_appointment_id: p.v2_appointment_id,
        v2_participant_id: p.v2_participant_id,
        participant_name: p.participant_name,
        scheduled_date: p.scheduled_date,
        title: p.title,
      });
      onOpenSession(created.id);
    } finally {
      setOpeningKey(null);
    }
  };

  const todayIso = useMemo(() => localTodayIso(), []);
  const loading = items == null || past == null;

  // One continuous time axis top→bottom: latest future first, the nearest
  // sessions just above the "Aujourd'hui" divider, then the past descending.
  const nextItems = items?.slice(0, PROMINENT_COUNT) ?? [];
  const laterItems = items?.slice(PROMINENT_COUNT) ?? [];
  const laterDesc = [...laterItems].reverse();
  const nextDesc = [...nextItems].reverse();

  const upcomingCard = (item: UpcomingTH, prominent: boolean) => {
    const opening = openingKey === keyFor(item);
    return (
      <div
        key={keyFor(item)}
        className={`timeline-item${prominent ? ' timeline-item--next' : ''}`}
      >
        <div
          className={`th-card th-card--cal${prominent ? ' th-card--next' : ''} ${opening ? 'th-card--opening' : ''}`}
          onClick={() => open(item)}
        >
          <CalDate iso={item.scheduled_date} lang={lang} />
          <div className="th-card-main">
            <div className="th-card-title">
              {item.source === 'bsp' && <span className="badge badge-bsp" style={{ marginRight: 6 }}>{t(item.meeting_kind === 'bsp' ? 'upcoming.badge.bspPlanned' : 'upcoming.badge.bilan')}</span>}
              {item.source === 'bsp' ? (item.participant_name ?? item.title) : item.title}
            </div>
            <div className="th-card-meta">
              {item.source === 'bsp' && formatTime(item.start_time) && <>{formatTime(item.start_time)}{' · '}</>}
              {item.source !== 'bsp' && item.location && <>{item.location}{' · '}</>}
              <span className="th-card-source">{sourceLabel(item.source)}</span>
              {whoLine(item.animator_user_id, item.animator_name)}
              {item.status === 'closed' && <> · <span className="badge badge-muted">{t('session.status.closed')}</span></>}
            </div>
            <NamesLine names={item.participant_names} />
          </div>
          <div className="th-card-side">
            {item.session_id != null ? (
              <FillPill recorded={item.recorded_count} total={item.participant_count} t={t} />
            ) : (
              (item.participant_count ?? 0) > 0 && item.source !== 'bsp' && (
                <span className="fill-pill fill-pill--muted">
                  {t('upcoming.enrolled').replace('{n}', String(item.participant_count))}
                </span>
              )
            )}
            <button className="btn btn-accent btn-sm" disabled={opening} onClick={(e) => { e.stopPropagation(); open(item); }}>
              {opening ? t('upcoming.opening') : t('upcoming.openSpace')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 style={{ margin: 0 }}>{t('upcoming.title')}</h1>
          <div className="scope-toggle" role="radiogroup" aria-label={t('upcoming.scope')}>
            <button type="button" role="radio" aria-checked={scope === 'mine'} className={`scope-toggle-btn ${scope === 'mine' ? 'scope-toggle-btn--active' : ''}`} onClick={() => setScope('mine')}>
              {t('upcoming.scope.mine')}
            </button>
            <button type="button" role="radio" aria-checked={scope === 'all'} className={`scope-toggle-btn ${scope === 'all' ? 'scope-toggle-btn--active' : ''}`} onClick={() => setScope('all')}>
              {t('upcoming.scope.all')}
            </button>
          </div>
        </div>
        <button className="btn btn-accent" onClick={() => setShowNew(true)}>
          <Plus size={14} /> {t('upcoming.newAdHoc')}
        </button>
      </div>

      {loading && <div className="empty-state">{t('common.loading')}</div>}

      {!loading && (
        <div className="timeline">
          <section>
            <h2 className="timeline-section-label">{t('upcoming.next')}</h2>

            {items!.length === 0 && (
              <div className="empty-state" style={{ marginBottom: 'var(--s16)' }}>{t('upcoming.empty')}</div>
            )}

            {laterItems.length > 0 && (
              <button
                className="timeline-expand"
                onClick={() => setShowAllUpcoming((v) => !v)}
                aria-expanded={showAllUpcoming}
              >
                {showAllUpcoming
                  ? <><ChevronDown size={14} /> {t('upcoming.showLess')}</>
                  : <><ChevronUp size={14} /> {t('upcoming.showMore').replace('{n}', String(laterItems.length))}</>}
              </button>
            )}

            {showAllUpcoming && laterDesc.map((item) => upcomingCard(item, false))}

            {nextDesc.map((item) => upcomingCard(item, true))}
          </section>

          <div className="timeline-today" role="separator">
            <span className="timeline-today-label">
              {t('upcoming.today')} · {formatDate(todayIso)}
            </span>
          </div>

          <section>
            <h2 className="timeline-section-label">{t('upcoming.past')}</h2>
            {past!.length === 0 && (
              <div className="empty-state">{t('upcoming.pastEmpty')}</div>
            )}
            {past!.map((p) => {
              const isBsp = p.session_type === 'bsp';
              const isIntake = p.session_type === 'intake_bsp';
              // An intake/BSP with a materialized séance opens like any other;
              // without one it is a passive historical marker.
              const passive = false;
              const opening = openingKey === pastKeyFor(p);
              return (
                <div key={pastKeyFor(p)} className="timeline-item timeline-item--past">
                  <div
                    className={`th-card th-card--cal th-card--past ${opening ? 'th-card--opening' : ''}`}
                    onClick={passive ? undefined : () => openPast(p)}
                    style={passive ? { cursor: 'default' } : undefined}
                  >
                    <CalDate iso={p.scheduled_date} lang={lang} />
                    <div className="th-card-main">
                      <div className="th-card-title">
                        {(isBsp || isIntake) && (
                          <span className="badge badge-bsp" style={{ marginRight: 6 }}>
                            {isIntake ? t('participant.objectives.intakeSource') : t('upcoming.badge.bilan')}
                          </span>
                        )}
                        {(isBsp || isIntake) ? (p.participant_name ?? p.title ?? '—') : (p.title ?? '—')}
                      </div>
                      {!passive && (
                        <div className="th-card-meta">
                          {(isBsp || isIntake) && formatTime(p.start_time) && <>{formatTime(p.start_time)}{' · '}</>}
                          {isIntake ? t('upcoming.source.intake') : isBsp ? t('upcoming.source.bsp') : (p.location ?? '—')}
                          {whoLine(p.animator_user_id, p.animator_name)}
                          {p.session_type === 'th' && p.session_id == null && <> · <span className="badge badge-muted">{t('upcoming.badge.unopened')}</span></>}
                          {p.status === 'closed' && <> · <span className="badge badge-muted">{t('session.status.closed')}</span></>}
                        </div>
                      )}
                      {!passive && <NamesLine names={p.participant_names} />}
                    </div>
                    {!passive && (
                      <div className="th-card-side">
                        <FillPill recorded={p.recorded_count} total={p.participant_count} t={t} />
                        <button className="btn btn-ghost btn-sm" disabled={opening} onClick={(e) => { e.stopPropagation(); openPast(p); }}>
                          {opening ? t('upcoming.opening') : t('upcoming.openSpace')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </section>

          {trash && trash.length > 0 && (
            <section className="trash-section">
              <button className="timeline-expand" onClick={() => setShowTrash((v) => !v)} aria-expanded={showTrash}>
                <Trash2 size={13} /> {t('upcoming.trash').replace('{n}', String(trash.length))}
              </button>
              {showTrash && trash.map((s) => (
                <div key={s.session_id} className="timeline-item timeline-item--past">
                  <div className="th-card th-card--cal th-card--past th-card--trashed">
                    <CalDate iso={s.scheduled_date} lang={lang} />
                    <div className="th-card-main">
                      <div className="th-card-title">{s.title ?? '—'}</div>
                      <div className="th-card-meta">
                        {s.animator_name}
                        {s.deleted_by_name && <> · {t('upcoming.deletedBy')} {s.deleted_by_name}</>}
                        {' · '}{t('upcoming.trashParticipants').replace('{n}', String(s.participant_count))}
                      </div>
                    </div>
                    <div className="th-card-side">
                      <button className="btn btn-sm btn-accent" onClick={() => restore(s.session_id)}>
                        <RotateCcw size={12} /> {t('upcoming.trashRestore')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </section>
          )}
        </div>
      )}

      {showNew && (
        <div className="modal-backdrop" onClick={() => setShowNew(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">{t('upcoming.modal.title')}</div>
            <div className="field">
              <label className="field-label">{t('upcoming.modal.type')}</label>
              <div className="presence-seg" role="radiogroup" aria-label={t('upcoming.modal.type')}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={newType === 'th'}
                  className={`presence-seg-btn presence-seg-btn--present ${newType === 'th' ? 'presence-seg-btn--active' : ''}`}
                  onClick={() => setNewType('th')}
                >
                  {t('upcoming.modal.typeEE')}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={newType === 'bsp'}
                  className={`presence-seg-btn presence-seg-btn--present ${newType === 'bsp' ? 'presence-seg-btn--active' : ''}`}
                  onClick={() => setNewType('bsp')}
                >
                  {t('upcoming.modal.typeBsp')}
                </button>
              </div>
            </div>
            <div className="field">
              <label className="field-label">{t('upcoming.modal.date')}</label>
              <input className="field-input" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">{t('upcoming.modal.titleField')}</label>
              <input className="field-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder={t('upcoming.modal.titlePlaceholder')} />
            </div>
            <div className="field">
              <label className="field-label">{t('upcoming.modal.location')}</label>
              <input className="field-input" value={newLocation} onChange={(e) => setNewLocation(e.target.value)} />
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setShowNew(false)}>{t('common.cancel')}</button>
              <button className="btn btn-accent" disabled={busy || !newTitle.trim() || !newDate} onClick={createAdHoc}>
                {busy ? t('upcoming.modal.creating') : t('upcoming.modal.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
